import { Helmet } from 'react-helmet';
import { Provider } from 'react-redux';

import { css, Global } from '@emotion/react';
import { BricksProvider } from '@opendoor/bricks-next';
import SelectiveSpritesheet from '@opendoor/bricks/core/Icon/SelectiveSpritesheet';
import ArrowDown from '@opendoor/bricks/core/Icon/SpritesheetIcons/ArrowDown';
import ArrowRight from '@opendoor/bricks/core/Icon/SpritesheetIcons/ArrowRight';
import Checkmark from '@opendoor/bricks/core/Icon/SpritesheetIcons/Checkmark';
import ChevronDown from '@opendoor/bricks/core/Icon/SpritesheetIcons/ChevronDown';
import ChevronDown16 from '@opendoor/bricks/core/Icon/SpritesheetIcons/ChevronDown16';
import ChevronLeft from '@opendoor/bricks/core/Icon/SpritesheetIcons/ChevronLeft';
import ChevronLeft16 from '@opendoor/bricks/core/Icon/SpritesheetIcons/ChevronLeft16';
import ChevronUp16 from '@opendoor/bricks/core/Icon/SpritesheetIcons/ChevronUp16';
import Close from '@opendoor/bricks/core/Icon/SpritesheetIcons/Close';
import Close16 from '@opendoor/bricks/core/Icon/SpritesheetIcons/Close16';
import Email from '@opendoor/bricks/core/Icon/SpritesheetIcons/Email';
import Info from '@opendoor/bricks/core/Icon/SpritesheetIcons/Info';
import InfoFilled from '@opendoor/bricks/core/Icon/SpritesheetIcons/InfoFilled';
import Minus from '@opendoor/bricks/core/Icon/SpritesheetIcons/Minus';
import Sign from '@opendoor/bricks/core/Icon/SpritesheetIcons/Sign';
import StarFilled from '@opendoor/bricks/core/Icon/SpritesheetIcons/StarFilled';
import { FontFace } from '@opendoor/bricks/styles/FontFace';
import { Reset } from '@opendoor/bricks/styles/Reset';
import { ThemedLite } from '@opendoor/bricks/theme/ODThemeLite';
import faviconPath from '@opendoor/shared-fe/build/faviconPath';

import { useObservability } from 'src/helpers/observability';
import { useWebViewLinkIntercept } from 'src/helpers/useWebViewLinkIntercept';

import renderRoutes from './routes';
import { history, store } from './store';

/*
 * This makes reception match with cosmos
 * - font smoothing makes our font-weights lighter
 * - box-sizing: border-box makes padding be included in the width which makes
 *   layouts easiser
 */
const AdditionalReset = () => (
  <Global
    styles={css`
      *,
      *:before,
      *:after {
        box-sizing: border-box;
        -webkit-font-smoothing: antialiased;
        -moz-osx-font-smoothing: grayscale;
      }
    `}
  />
);

const App: React.FC = () => {
  useWebViewLinkIntercept();

  return (
    <div>
      <Helmet>
        <title>Opendoor | Offer request steps</title>
        <meta charSet="utf-8" />
        <meta httpEquiv="X-UA-Compatible" content="IE=edge,chrome=1" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <meta name="fragment" content="!" />
        <meta httpEquiv="Accept-CH" content="DPR, Width, Viewport-Width" />
        <meta name="author" content="opendoorcode" />
        <link rel="shortcut icon" href={faviconPath()} type="image/png" />
        <link
          rel="apple-touch-icon"
          sizes="144x144"
          href="https://www.opendoor.com/apple-touch-icon-144.png"
        />
      </Helmet>
      <SelectiveSpritesheet
        icons={[
          ArrowRight,
          ArrowDown,
          Checkmark,
          ChevronDown,
          ChevronDown16,
          ChevronLeft,
          ChevronLeft16,
          ChevronUp16,
          Close,
          Close16,
          Email,
          Info,
          InfoFilled,
          Sign,
          StarFilled,
          Minus,
        ]}
      />
      <ThemedLite theme="novo">
        <BricksProvider useObservability={useObservability}>
          <Reset />
          <AdditionalReset />
          <FontFace />
          <Provider store={store}>{renderRoutes(history)}</Provider>
        </BricksProvider>
      </ThemedLite>
    </div>
  );
};

export default App;
